import {
  BodyText,
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Search,
} from "@/components/ui";
import { SearchIcon, Trash2 } from "lucide-react";
import { useState } from "react";
import { Controller, type UseFormReturn, useFieldArray } from "react-hook-form";
import type { z } from "zod";
import { useLoincSearch } from "../../network/useLoincSearch";
import { useSubmitManualResults } from "../../network/useSubmitManualResults";
import type { manualResultFormSchema } from "../../schema";

export const ManualResultForm = ({
  patientId,
  serviceRequestId,
  diagnosticReportId,
  disabled,
  form,
}: {
  patientId: string;
  serviceRequestId: string;
  diagnosticReportId: string;
  disabled?: boolean;
  form: UseFormReturn<z.infer<typeof manualResultFormSchema>>;
}) => {
  const [loincSearchTerm, setLoincSearchTerm] = useState("");

  const { mutate: submitManualResults, isPending: isSubmitting } =
    useSubmitManualResults({
      patientId,
      serviceRequestId,
      diagnosticReportId,
    });

  const { data: loincResults } = useLoincSearch({
    searchText: loincSearchTerm,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "resultsList",
  });

  const onSubmit = (data: z.infer<typeof manualResultFormSchema>) => {
    submitManualResults({
      results: data.resultsList,
    });
  };

  return (
    <div className="flex flex-col gap-2 max-w-[400px]">
      <div className="flex flex-col gap-2">
        <Search
          placeholder="Search for Finding"
          disabled={disabled}
          onInputChange={setLoincSearchTerm}
          leftIcon={SearchIcon}
          input={loincSearchTerm}
          value={loincSearchTerm}
          options={
            loincResults?.map((result) => ({
              label: result.display,
              value: result.code,
            })) ?? []
          }
          onItemSelected={(item) => {
            append({
              code: item.value,
              codeDisplay: item.label,
              resultValue: "",
            });
            setLoincSearchTerm("");
            form.trigger("resultsList");
            form.reset(form.getValues(), {
              keepDirty: true,
              keepErrors: false,
              keepIsSubmitted: false,
            });
          }}
          shouldFilter={false}
          clearOnSelect={true}
        />
      </div>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-2"
      >
        {fields.map((manualResultSubformField, index) => {
          return (
            <div
              key={manualResultSubformField.id}
              className="flex flex-row items-center"
            >
              <Controller
                control={form.control}
                name={`resultsList.${index}.resultValue`}
                render={({ field, fieldState }) => (
                  <div>
                    <Field className="flex flex-row gap-2 items-center">
                      <FieldLabel
                        htmlFor={`resultsList.${index}.resultValue`}
                        className={disabled ? "text-tertiary" : ""}
                      >
                        {manualResultSubformField.codeDisplay}
                      </FieldLabel>
                      <Input
                        id={`resultsList.${index}.resultValue`}
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          form.trigger("resultsList");
                        }}
                        placeholder="Enter result value with units"
                        disabled={disabled}
                        autoFocus
                      />
                    </Field>
                    {form.formState.isSubmitted && (
                      <FieldError
                        errors={[fieldState.error]}
                        className="w-full text-right"
                      />
                    )}
                  </div>
                )}
              />
              <Button
                variant="icon"
                size="xs"
                onClick={() => {
                  remove(index);
                  form.trigger("resultsList");
                }}
                disabled={disabled}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          );
        })}

        {(form.formState.errors?.resultsList?.root?.message ||
          form.formState.errors?.resultsList?.message) && (
          <BodyText size="xs" className="text-error">
            {form.formState.errors?.resultsList?.root?.message ||
              form.formState.errors?.resultsList?.message}
          </BodyText>
        )}
        <Button
          size="xs"
          type="submit"
          className="w-fit"
          disabled={disabled}
          isLoading={isSubmitting}
        >
          Submit Results
        </Button>
      </form>
    </div>
  );
};
